import React, { useEffect, useState } from "react";
import { DateTime } from "luxon";
import BeatLoader from "react-spinners/BeatLoader";
import {
  getAppointmentDays,
  getAppointmentSlots,
  getFirstAvailableAppointment
} from "../../services/appointment.service";
import { useMediaQuery } from "../../hooks/useMediaQuery";

// Date and time selection (plan §5.2). Every availability decision is made on the
// server: this component picks a date, asks for that date's slots, and renders what
// comes back. It deliberately performs **no** client-side availability computation —
// §15.5 wants the §3.3 precedence chain to exist in exactly one place, and anything
// resembling `isDayClosed` here would have to reimplement it.

// How many days the strip shows at once — and therefore how far each arrow pages. A
// phone cannot fit a week legibly, and the strip must never scroll: a day hidden past
// the edge of a scroller with no visible scrollbar is a day the guest never learns
// about, and then the "later" arrow appears to skip it.
// Breakpoints are where a row of dates stops fitting beside the two nav arrows, not
// where the rest of the app calls something "mobile" — hence the odd-looking widths.
const NARROW = "(max-width: 519px)";
const VERY_NARROW = "(max-width: 399px)";

type Slot = {
  time: string;
  isAvailable: boolean;
  resourceId?: string;
  resourceName?: string;
};

/** One day of the booking window; `closed` means nobody works it at all. */
type DayAvailability = {
  date: string;
  closed: boolean;
};

const SelectDateTimeStep = ({
  business,
  serviceId,
  serviceName,
  resourceId,
  resourceName,
  onSelect,
  buttonStyle,
  onError
}) => {
  const timezone = business?.settings?.timezone;
  const now = DateTime.now().setZone(timezone);

  const isNarrow = useMediaQuery(NARROW);
  const isVeryNarrow = useMediaQuery(VERY_NARROW);
  const daysVisible = isVeryNarrow ? 4 : isNarrow ? 5 : 7;

  // Bookable date range. Both bounds are flat business settings already in the
  // checkInData payload, so this is arithmetic — no extra request, and no client-side
  // reimplementation of the §3.3 hours chain, which stays on the server (plan §15.5).
  // A day counts as reachable if *any* instant on it clears the lead time, hence the
  // startOf("day") — a 2-hour lead at 10pm still leaves tonight bookable.
  const leadValue = Number(business?.settings?.leadTimeValue || 0);
  const leadUnits = business?.settings?.leadTimeUnits || "minutes";
  const firstDay = (leadValue ? now.plus({ [leadUnits]: leadValue }) : now).startOf("day");

  const windowValue = Number(business?.settings?.scheduleWindowValue || 30);
  const windowUnits = business?.settings?.scheduleWindowUnits || "days";
  const lastDay = now.plus({ [windowUnits]: windowValue }).startOf("day");

  const [rangeStart, setRangeStart] = useState(firstDay);
  const [selectedDate, setSelectedDate] = useState(firstDay);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [closedDates, setClosedDates] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [jumping, setJumping] = useState(true);

  // Days nobody works are greyed alongside the lead-time and window bounds. The set is
  // empty until the server answers, so nothing flickers grey-then-open on first paint.
  const isBookableDay = (day: DateTime) =>
    day >= firstDay && day <= lastDay && !closedDates.has(day.toISODate() as string);

  const unbookableReason = (day: DateTime) => {
    if (day < firstDay) return "Too soon to book";
    if (day > lastDay) return "Too far ahead to book";
    return "Closed";
  };

  // Jump straight to the first day with an opening, so a resource who does not work
  // for the next fortnight does not present the guest with an empty grid. The closed-day
  // map is fetched alongside it — same trigger, and the two requests run concurrently.
  useEffect(() => {
    let cancelled = false;
    const jump = async () => {
      setJumping(true);
      const scope = { businessId: business.id, date: firstDay.toISODate(), serviceId, resourceId };
      try {
        const [first, days] = await Promise.all([getFirstAvailableAppointment(scope), getAppointmentDays(scope)]);
        if (cancelled) return;

        setClosedDates(
          new Set((days?.data || []).filter((d: DayAvailability) => d.closed).map((d: DayAvailability) => d.date))
        );

        if (first?.data?.date) {
          const firstOpen = DateTime.fromISO(first.data.date, { zone: timezone }).startOf("day");
          setSelectedDate(firstOpen);
          setRangeStart(firstOpen);
        }
      } catch (e) {
        if (!cancelled) onError?.((e as Error)?.message);
      }
      if (!cancelled) setJumping(false);
    };
    jump();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, resourceId]);

  useEffect(() => {
    if (jumping) return;
    // Unreachable by clicking — those days are disabled — but a lead time longer than
    // the booking window leaves nothing bookable at all, and the server would only
    // reject the request anyway.
    if (!isBookableDay(selectedDate)) {
      setSlots([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getAppointmentSlots({
          businessId: business.id,
          date: selectedDate.toISODate(),
          serviceId,
          resourceId
        });
        if (!cancelled) setSlots(res?.data || []);
      } catch (e) {
        if (!cancelled) {
          setSlots([]);
          onError?.((e as Error)?.message);
        }
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, jumping]);

  // The strip always renders a full page of days and greys what is out of range, rather
  // than dropping those days — a short strip near the window edge looks like a rendering
  // bug, whereas a disabled day reads as "not this one".
  const days = Array.from({ length: daysVisible }, (_, i) => rangeStart.plus({ days: i }));
  const canGoBack = rangeStart > firstDay;
  const canGoForward = rangeStart.plus({ days: daysVisible }) <= lastDay;
  const available = slots.filter(s => s.isAvailable);

  return (
    <div className="appointments__step">
      <div className="self-reservations__title">Select a date and time</div>
      {/* Either half may be absent when the business defined only services or only
          resources, so this joins whatever it was given rather than assuming both. A
          separator, not "with" — the preposition depends on whether the resource is a
          person, a room, or a lane. */}
      <div className="appointments__selection-summary">{[serviceName, resourceName].filter(Boolean).join(" · ")}</div>

      <div className="appointments__date-strip">
        <button
          type="button"
          className="appointments__date-nav"
          disabled={!canGoBack}
          onClick={() => setRangeStart(DateTime.max(firstDay, rangeStart.minus({ days: daysVisible })))}
          aria-label="Earlier dates"
        >
          ‹
        </button>
        <div className="appointments__dates">
          {days.map(day => {
            const bookable = isBookableDay(day);
            const isSelected = bookable && day.hasSame(selectedDate, "day");
            return (
              <button
                key={day.toISODate()}
                type="button"
                disabled={!bookable}
                title={bookable ? undefined : unbookableReason(day)}
                className={`appointments__date${isSelected ? " appointments__date--selected" : ""}`}
                style={isSelected ? buttonStyle : undefined}
                onClick={() => setSelectedDate(day)}
              >
                <span className="appointments__date-weekday">{day.toFormat("ccc")}</span>
                <span className="appointments__date-day">{day.toFormat("d")}</span>
                <span className="appointments__date-month">{day.toFormat("LLL")}</span>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="appointments__date-nav"
          disabled={!canGoForward}
          onClick={() => setRangeStart(rangeStart.plus({ days: daysVisible }))}
          aria-label="Later dates"
        >
          ›
        </button>
      </div>

      {loading || jumping ? (
        <div className="appointments__loading">
          <BeatLoader margin={2.5} size={15} color={buttonStyle?.backgroundColor} />
        </div>
      ) : available.length ? (
        <div className="appointments__slots">
          {available.map(slot => (
            <button
              key={slot.time}
              type="button"
              className="appointments__slot"
              onClick={() =>
                onSelect({
                  date: selectedDate.toISODate(),
                  time: slot.time,
                  resourceId: slot.resourceId,
                  resourceName: slot.resourceName
                })
              }
            >
              {DateTime.fromFormat(slot.time, "HH:mm").toFormat("h:mm a")}
            </button>
          ))}
        </div>
      ) : (
        <div className="appointments__empty">
          No times available on {selectedDate.toFormat("LLLL d")}. Try another date.
        </div>
      )}
    </div>
  );
};

export default SelectDateTimeStep;
